<template>
  <div class="patient-editors" data-testid="patient-dialogue-editor-container">
    <div class="old-globals two-column-grid">
      <label for="voice">Voice</label>
      <voice-selector
        :voice-key="caseConf.voiceKey"
        :voices="voices"
        :readonly="readonly"
        :showLabel="false"
        aria-label="Patient Voice Selector"
        data-testid="voice-selector"
        display-row
        :disabled-voice-keys="disabledForPatient().map(info => info.voiceKey)"
        @update="updatePatientVoice"
      />
      <label v-if="activeTab === DialogueType.AI_PATIENT_PROMPT" for="patient-role-input">
        Role
      </label>
      <div v-if="activeTab === DialogueType.AI_PATIENT_PROMPT">
        <md-field :class="{'md-invalid': false}">
          <md-input
            id="patient-role-input"
            aria-label="Patient Role"
            data-testid="role-input"
            value="Patient"
            :class="{'error': false}"
            disabled
            @input="updateRole"
          />
        </md-field>
      </div>
    </div>
    <dialogue-editor
      v-if="activeTab === DialogueType.Q_AND_A"
      :key="'PATIENT_QANDA'"
      class="new-globals"
      data-testid="questions-and-answers-container"
      :settings="questionSettings"
      :items="caseConf.dialogue.questions || []"
      :saved-items="questions"
      :readonly="readonly"
      :errors="errors"
      :warnings="warnings"
      @update="updateQuestions"
      @playAudio="(item) => playAnswerText(item, caseConf.voiceKey)"
    />
    <dialogue-editor
      v-if="activeTab === DialogueType.COMMANDS"
      :key="'PATIENT_COMMANDS'"
      class="new-globals"
      data-testid="commands-container"
      :settings="commandSettings"
      :items="caseConf.dialogue.commands || []"
      :readonly="readonly"
      :errors="errors"
      :warnings="warnings"
      @update="updateCommands"
      @playAudio="(item) => playAnswerText(item, caseConf.voiceKey)"
    />
    <dialogue-editor
      v-if="activeTab === DialogueType.UNSOLICITED"
      :key="'PATIENT_UNSOLICITED'"
      class="new-globals"
      data-testid="unsolicited-container"
      :settings="unsolicitedSettings"
      :items="caseConf.dialogue.unsolicited || []"
      :readonly="readonly"
      :errors="errors"
      :warnings="warnings"
      @update="updateUnsolicited"
      @playAudio="(item) => playAnswerText(item, caseConf.voiceKey)"
    />

    <div
      v-if="activeTab === DialogueType.AI_PATIENT_PROMPT"
      class="patient-prompt"
      data-testid="prompt-container"
    >
      <html-editor
        label="Patient Prompt"
        subtitle="Patient prompt should cover pertinent patient history, demographic information, and details on current circumstances."
        placeholder="Write or paste your patient prompt here"
        data-testid="prompt-container"
        :contents="promptMarkup"
        :readonly="readonly"
        :reload="state.reloadPrompt"
        :errors="errors.filter(error => error.metadata.dialogueType === DialogueType.AI_PATIENT_PROMPT)"
        :prevent-emojis="true"
        @update="debouncedEditPrompt"
      />
      <prompt-footer
        :case-conf="caseConf"
        :readonly="readonly"
        @updateUseCurrentDate="updateUseCurrentDate"
        @replacePrompt="replacePrompt"
      />
    </div>
    <div
      v-if="activeTab !== DialogueType.AI_PATIENT_PROMPT"
      class="ai-directed-dialogue"
      data-testid="ai-listen-for-say-this-container"
    >
      <dialogue-editor
        v-if="activeTab === DialogueType.AI_DIRECTED_DIALOGUE"
        key="AI_QUESTIONS"
        class="ai-questions new-globals"
        data-testid="ai-listen-for-container"
        :settings="aiQuestionSettings"
        :items="caseConf.dialogue.aiQuestions || []"
        :saved-items="questions"
        :readonly="readonly"
        :errors="errors"
        :warnings="warnings"
        @update="updateAiQuestions"
        @playAudio="(item) => playAnswerText(item, caseConf.voiceKey)"
      />
      <dialogue-editor
        v-if="activeTab === DialogueType.AI_DIRECTED_DIALOGUE"
        key="AI_UNSOLICITED"
        class="ai-unsolicited new-globals"
        data-testid="ai-say-this-container"
        :settings="aiUnsolicitedSettings"
        :items="caseConf.dialogue.aiUnsolicited || []"
        :readonly="readonly"
        :errors="errors"
        :warnings="warnings"
        @update="updateAiUnsolicited"
        @playAudio="(item) => playAnswerText(item, caseConf.voiceKey)"
      />
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import get from 'lodash/get';

import HtmlEditor from '~lib/common/components/HtmlEditor/HtmlEditor.vue';

import VoiceSelector from '../common/VoiceSelector.vue';
import DialogueEditor from './DialogueEditor.vue';
import PromptFooter from './PromptFooter.vue';
import DialogueType from './DialogueType';

export default {
  name: 'patient-editors',
  components: {
    VoiceSelector,
    DialogueEditor,
    HtmlEditor,
    PromptFooter,
  },
  props: {
    activeId: {
      type: String,
      required: true,
    },
    activeTab: {
      type: String,
      required: true,
    },
    caseConf: {
      type: Object,
      required: true,
    },
    voices: {
      type: Array,
      default: () => []
    },
    questions: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Array,
      default: () => [],
    },
    warnings: {
      type: Array,
      default: () => [],
    },
    usedInfo: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      questionSettings: {
        type: DialogueType.Q_AND_A,
        onText: 'Patient: On-at-Start Questions',
        offText: 'Patient: Off-at-Start Questions',
        questionLabel: 'Question',
        answerLabel: 'Answer',
        newQuestionLabel: 'Add a question',
        newAnswerLabel: 'Add an answer',
      },
      commandSettings: {
        type: DialogueType.COMMANDS,
        onText: 'Patient: On-at-Start Commands',
        offText: 'Patient: Off-at-Start Commands',
        onSubtitle: 'Commands need to be connected to their desired effects in Case Logic',
        questionLabel: 'Command',
        newQuestionLabel: 'Add a command',
        isAnswerDisabled: true,
      },
      unsolicitedSettings: {
        type: DialogueType.UNSOLICITED,
        offText: 'Patient: Unsolicited',
        offSubtitle: 'Unsolicited needs to be triggered by an action or timer in Case Logic',
        onDisabled: true,
        isQuestionDisabled: true,
        answerLabel: 'Patient Unsolicited',
        newAnswerLabel: 'Add a question or statement to say'
      },
      aiQuestionSettings: {
        type: DialogueType.Q_AND_A,
        onText: 'AI: Listen For & Say This',
        onSubtitle: 'Have the AI listen for a specific question and respond with a specific answer',
        offText: 'AI: Say This',
        questionLabel: 'Listen For',
        answerLabel: 'Say This',
        reasonLabel: 'Reason for Response',
        newQuestionLabel: 'Add a question or statement to listen for',
        newAnswerLabel: 'Add a question or answer to be said',
        newReasonLabel: 'Add a reason for this response',
        offDisabled: true,
        isAI: true,
        fullWidth: true,
      },
      aiUnsolicitedSettings: {
        type: DialogueType.UNSOLICITED,
        offText: 'AI: Say This',
        offSubtitle: 'Have the AI make specific statements through scenario logic',
        onDisabled: true,
        isQuestionDisabled: true,
        answerLabel: 'Say This',
        reasonLabel: 'Reason for Response',
        newAnswerLabel: 'Add a question or statement to say',
        newReasonLabel: 'Add a reason for this response',
        isAI: true,
        fullWidth: true,
      },
      state: {
        reloadPrompt: false,
      },
      DialogueType,
    };
  },
  computed: {
    promptMarkup() {
      return get(this.caseConf, 'dialogue.patientPrompt.markup.en');
    },
  },
  methods: {
    disabledForPatient() {
      return this.usedInfo.filter(info => info.key !== 'PATIENT');
    },
    updatePatientVoice(voiceKey) {
      this.$emit('update', { voiceKey });
    },
    updateRole(value) {
      console.log(value)
    },
    updateQuestions(items) {
      this.$emit('update', {
        dialogue: {
          questions: items,
        },
      });
    },
    updateCommands(items) {
      this.$emit('update', {
        dialogue: {
          commands: items,
        },
      });
    },
    updateUnsolicited(items) {
      this.$emit('update', {
        dialogue: {
          unsolicited: items,
        }
      });
    },
    updateAiQuestions(items) {
      this.$emit('update', {
        dialogue: {
          aiQuestions: items
        },
      });
    },
    updateAiUnsolicited(items) {
      this.$emit('update', {
        dialogue: {
          aiUnsolicited: items
        },
      });
    },
    playAnswerText(question, withVoiceId) {
      this.$emit('playAnswerText', question, withVoiceId);
    },
    editPrompt(value) {
      this.$emit('update', {
        dialogue: {
          patientPrompt: {
            markup: {
              en: value.markup
            },
            content: {
              en: value.content
            }
          }
        }
      });
    },
    debouncedEditPrompt: debounce(function(value) {
      this.editPrompt(value);
    }, 500),
    updateUseCurrentDate(value) {
      this.$emit('update', {
        dialogue: {
          useCurrentDate: value
        }
      });
    },
    replacePrompt(value) {
      this.editPrompt(value);
      // A hacky way to get the HtmlEditor to reload its contents
      this.state.reloadPrompt = true;
      this.$nextTick(() => {
        this.state.reloadPrompt = false;
      });
    },
  }
}
</script>

<style scoped>
.patient-editors {
  height: 100%;
  padding-top: 12px;
}

.patient-prompt {
  display: flex;
  flex-direction: column;
  height: calc(100% - 100px);
}

.html-editor {
  flex: 1;
}

.ai-directed-dialogue {
  display: flex;
  height: calc(100% - 40px);
}

.ai-unsolicited {
  margin-left: 15px;
}

.ai-directed-dialogue .list-container {
  flex: 1;
}

.two-column-grid {
  width: 480px;
  margin-bottom: 1rem;
}
</style>